import auth from '@client/components/auth';
import { useAsyncResult } from '@client/new/helpers/gql';
import { Actions, Async, F1 } from '@client/new/helpers/types';
import { isNil } from 'lodash';
import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  GetOtpDefaultFeeQuery,
  useGetOtpDefaultFeeQuery,
  useSetOtpOfferDefaultFeeMutation,
} from '../../../../../../generated/graphql';

type OtpCta = {
  label: string;
  onClick: () => void;
  variant: 'outlined' | 'contained' | 'text';
};

type useRevenueProps = {
  influencerId: number;
};

type OtpModel = {
  fee: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled: boolean;
  cta: Array<OtpCta>;
};
export type RevenueViewActionsMap = {
  cancelEditOtpFee: never;
  saveOtpFee: never;
  editOtpFee: never;
  changeFee: number | undefined;
};
export type RevenueViewActions = Actions<RevenueViewActionsMap>;
export type OtpViewDispatcher = F1<RevenueViewActions>;

const getCta = (
  dispatch: OtpViewDispatcher,
  isEditMode: boolean,
): Array<OtpCta> => {
  if (isEditMode) {
    return [
      {
        label: 'Cancel',
        onClick: () => dispatch({ type: 'cancelEditOtpFee' }),
        variant: 'outlined',
      },
      {
        label: 'Save',
        onClick: () => dispatch({ type: 'saveOtpFee' }),
        variant: 'contained',
      },
    ];
  }
  return [
    {
      label: 'Edit',
      onClick: () => dispatch({ type: 'editOtpFee' }),
      variant: 'contained',
    },
  ];
};

export const useOtpOfferFee = ({
  influencerId,
}: useRevenueProps): Async<OtpModel> => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [fee, setFee] = useState<number | undefined>();
  const [isFeeInitialized, setIsFeeInitialized] = useState(false);

  const otpDefaultFeeQuery = useGetOtpDefaultFeeQuery({
    variables: {
      id: influencerId,
    },
  });

  const [setOtpOfferDefaultFeeMutation] = useSetOtpOfferDefaultFeeMutation({
    variables: {
      data: {
        fee,
        influencerId,
      },
    },
  });

  const saveOTP = async () => {
    return setOtpOfferDefaultFeeMutation()
      .then(() => {
        toast.success('Fee was updated successfully');
        otpDefaultFeeQuery.refetch();
      })
      .catch(() => {
        toast.error('Something went wrong with saving fee');
        setFee(
          otpDefaultFeeQuery.data?.creator?.otpBundleDefaultFee || undefined,
        );
      });
  };

  const setInitialFee = () =>
    setFee(otpDefaultFeeQuery.data?.creator?.otpBundleDefaultFee || undefined);

  const dispatch: OtpViewDispatcher = (action) => {
    switch (action.type) {
      case 'cancelEditOtpFee':
        setIsEditMode(false);
        return setInitialFee();
      case 'saveOtpFee':
        setIsEditMode(false);
        return saveOTP();
      case 'editOtpFee':
        return setIsEditMode(true);
      case 'changeFee':
        return setFee(action.payload);
    }
  };

  const asyncOtpDefaultFee =
    useAsyncResult<GetOtpDefaultFeeQuery>(otpDefaultFeeQuery);
  if (asyncOtpDefaultFee.type !== 'fetched') return asyncOtpDefaultFee;

  if (isNil(asyncOtpDefaultFee.value.creator?.otpBundleDefaultFee))
    return { type: 'fetch-error', value: 'Could not fetch data' };

  if (asyncOtpDefaultFee.type === 'fetched' && !isFeeInitialized) {
    setIsFeeInitialized(true);
    setFee(asyncOtpDefaultFee.value.creator?.otpBundleDefaultFee);
  }

  const hasPayoutPermission = auth.hasPayoutsPermission();

  return {
    type: 'fetched',
    value: {
      fee,
      onChange: (payload) => dispatch({ type: 'changeFee', payload }),
      disabled: !isEditMode,
      cta: hasPayoutPermission ? getCta(dispatch, isEditMode) : [],
    },
  };
};
